/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    SidebarMenuAction,
    SidebarMenuButton,
    SidebarMenuItem,
    useIsMobile,
    Input,
    Button,
} from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { RiLoader4Line } from '@remixicon/react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/es';
import { MessageSquare, MoreHorizontal, Edit, Trash2, Save, X } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUpdateConversationTitle } from '../../../../../hooks/useUpdateConversationTitle';
import { useChatContext } from '../../../../../providers/Chat/Chat.provider';
import { ConversationItem } from '../../../../../types/Conversation/Conversation';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.locale('es');

export function ChatItem({
    item,
    setDeleteOpen,
    setId,
    ...props
}: {
    item: ConversationItem;
    setDeleteOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setId: React.Dispatch<React.SetStateAction<string>>;
} & React.ComponentPropsWithoutRef<typeof SidebarMenuItem>) {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const queryClient = useQueryClient(); // Access the query client
    const [value, setValue] = useState(item.title ?? 'Sin titulo');
    const [edit, setEdit] = React.useState(false);
    const {
        load,
        setLoad,
        setConversationId,
        isPending: isQuestionPending,
        isStream: isQuestionStream,
    } = useChatContext();
    const isBusy = isQuestionPending || isQuestionStream || load;
    const { mutate: updateConversationTitle, isPending } = useUpdateConversationTitle();
    const { t } = useTranslation();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']);
    const currentOrganization = userInfo?.organizations.find((org) => org.id === userInfo.currentOrganization);

    const handleLoadConversation = (id: string) => {
        if (load || isQuestionPending || isQuestionStream) return;
        // Prevent loading if already loading or a chat operation is in progress
        setConversationId(id);
        setLoad(true);
        navigate(`/chat/${id}`);
    };
    const handleUpload = useCallback(() => {
        updateConversationTitle(
            { newTitle: value, data: item },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({ queryKey: ['conversations'] }).then(() => {
                        setEdit(false);
                    });
                },
            },
        );
    }, [item, queryClient, updateConversationTitle, value]);

    return (
        <SidebarMenuItem {...props} data-testid="chat-item">
            {edit ? (
                <div className="flex items-center space-x-2 px-2">
                    <Input
                        className="focus-visible:!ring-0 focus-visible:!ring-offset-0 text-sm px-2 !py-1 h-9"
                        placeholder={t('sideBar.chatList.edit.placeholder')}
                        value={value}
                        onChange={(evt) => setValue(evt.target.value)}
                        autoFocus
                    />
                    <Button size="sm" onClick={handleUpload} disabled={isPending}>
                        {isPending ? <RiLoader4Line size={14} className="animate-spin" /> : <Save size={14} />}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEdit(false)}>
                        <X size={14} />
                    </Button>
                </div>
            ) : (
                <>
                    <SidebarMenuButton asChild>
                        <button
                            className={`flex items-center w-full space-x-1 !h-auto py-1.5 ${
                                isBusy ? 'cursor-not-allowed' : ''
                            }`}
                            onClick={() => handleLoadConversation(item.conversationId)}
                        >
                            <MessageSquare className="text-muted-foreground flex-shrink-0" />
                            <div className="flex flex-col">
                                <span className="font-medium text-sm w-[170px] truncate" title={item.title}>
                                    {item.title}
                                </span>
                                <span className="text-muted-foreground text-xs font-light">
                                    {dayjs(item.created_at)
                                        .tz(currentOrganization?.timezone || 'America/Argentina/Buenos_Aires')
                                        .fromNow()}
                                </span>
                            </div>
                        </button>
                    </SidebarMenuButton>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <SidebarMenuAction
                                showOnHover
                                className={`${isBusy ? 'cursor-not-allowed' : ''}`}
                                onClick={(e) => {
                                    if (isBusy) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }
                                }}
                            >
                                <MoreHorizontal />
                                <span className="sr-only">More</span>
                            </SidebarMenuAction>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            className="w-48"
                            side={isMobile ? 'bottom' : 'right'}
                            align={isMobile ? 'end' : 'start'}
                        >
                            <DropdownMenuItem onClick={() => setEdit(true)} disabled={isBusy}>
                                <Edit className="text-muted-foreground" />
                                <span>{t('sideBar.chatList.edit.label')}</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                disabled={isBusy}
                                onClick={() => {
                                    setDeleteOpen(true);
                                    setId(item.conversationId);
                                }}
                            >
                                <Trash2 className="text-muted-foreground" />
                                <span>{t('sideBar.chatList.delete')}</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </>
            )}
        </SidebarMenuItem>
    );
}
